'use strict';

const libPictProvider = require('pict-provider');
const libDocumentFrame = require('headlight-configurable-application/source/providers/DocumentFrameProvider.js');

const CHANNEL = libDocumentFrame.CHANNEL;
const FRAME_PARAMETER = libDocumentFrame.FRAME_PARAMETER;

// Frames past this are closed least-recently-viewed first, but only when they are loaded and hold nothing unsaved.
const MAX_LIVE_FRAMES = 6;
const REQUEST_TIMEOUT_MS = 120000;
const LAYER_ID = 'DocFrames-Layer';
// Media kind -> [ singular, plural, Font Awesome icon ].
const MEDIA_LABELS =
{
	image: [ 'image', 'images', 'fa-image' ],
	video: [ 'video', 'videos', 'fa-video' ],
	audio: [ 'audio clip', 'audio clips', 'fa-microphone' ],
	file: [ 'file', 'files', 'fa-paperclip' ],
};

/**
 * @typedef {Object} SwitcherDocument
 * @property {number} IDDocument
 * @property {String} [Name]
 * @property {String} [DocumentType]
 * @property {String} [Status]
 * @property {String} [CreateDate]
 * @property {String} [FormName] - The form the document was made from, shown in its tooltip and, when it has no tests, on its second line.
 * @property {Array<SwitcherTest>} [Tests] - The tests the document covers; replaces the type on the rail.
 * @property {Record<String, number>} [Media] - Media observations by kind: image, video, audio, file.
 */

/**
 * @typedef {Object} SwitcherTest
 * @property {String} Code
 * @property {String} [Name]
 * @property {boolean} [Assigned] - The record asked for this test on this document.
 * @property {String} [Result] - The test's result as text, e.g. `Passed`.
 * @property {String} [ResultTone] - 'success' | 'danger' | 'warning' | 'info' | 'muted'.
 */

/**
 * @typedef {Object} FrameEntry
 * @property {number} IDDocument
 * @property {String} FrameID
 * @property {String} Phase - 'rendering' | 'loading' | 'ready' | 'error'
 * @property {String} Kind - 'pending' | 'form' (reports its state) | 'untracked' (a form that does not) | 'classic' (a legacy report page)
 * @property {String} Error
 * @property {?HTMLIFrameElement} IFrame
 * @property {?import('headlight-configurable-application/source/providers/DocumentFrameProvider.js').DocumentFrameState} State
 * @property {boolean} Touched - The user typed in a frame that cannot report its own unsaved state.
 * @property {number} LastActiveAt
 * @property {{ ClickedAt: number, RenderMS: ?number, LoadMS: ?number, ReadyMS: ?number, FormLoad: any }} Timing
 */

/**
 * DocumentFrameSwitcher — opens a record's documents side by side with a quick-switch rail, each in its own iframe running
 * the same form page the longhand editor opens (Report/Render → Report/{GUID}/index.html). Every document keeps its own
 * pict, so editing, saving, approval actions and access control are exactly the longhand path's, and a document that is
 * not on screen shares nothing with the one that is.
 *
 * Frames live in one layer appended to <body>, laid over an anchor element the caller paints, because a workspace repaint
 * replaces its markup and an iframe that leaves the DOM reloads (losing unsaved work). Switching only changes which frame
 * is visible. Configurable forms report ready / unsaved / approval state over the DocumentFrame protocol
 * (headlight-configurable-application DocumentFrameProvider); classic report pages are hosted the same way without it.
 */
class DocumentFrameSwitcherProvider extends libPictProvider
{
	/**
	 * @param {any} pFable - The Fable instance.
	 * @param {Object} [pOptions] - The provider options.
	 * @param {String} [pServiceHash] - The fable service hash for this instance.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;

		/** @type {?String} */
		this.OwnerKey = null;
		/** @type {(pHash: String) => boolean} */
		this._ownerMatch = () => false;
		/** @type {String} */
		this._anchorID = '';
		/** @type {Array<SwitcherDocument>} */
		this.Documents = [];
		/** @type {String} Plain text shown at the foot of the rail, e.g. how many documents the user cannot see. */
		this.RailNote = '';
		/** @type {Record<number, FrameEntry>} */
		this.Frames = {};
		/** @type {Record<String, FrameEntry>} */
		this._framesByFrameID = {};
		/** @type {number} */
		this.ActiveID = 0;
		/** @type {boolean} */
		this.Fullscreen = false;
		/** @type {Record<String, String>} - DocumentType -> Kind, learned from frames opened this session. */
		this._kindByType = {};
		/** @type {Record<String, { resolve: Function, timer: any }>} */
		this._pendingRequests = {};
		this._requestSerial = 0;
		this._frameSerial = 0;
		/** @type {Array<any>} */
		this.Timings = [];
		this._layer = null;
		/** @type {?String} The rail markup last painted, so an unchanged rail keeps its elements and an open tooltip. */
		this._railMarkup = null;
		/** @type {Array<{ destroy: Function }>} */
		this._railTooltips = [];
		this._rafHandle = null;
		this._lastHash = (typeof window !== 'undefined') ? window.location.hash : '';
		this._restoringHash = false;

		if (typeof window !== 'undefined')
		{
			this._onMessage = this._onMessage.bind(this);
			this._onHashChange = this._onHashChange.bind(this);
			this._onBeforeUnload = this._onBeforeUnload.bind(this);
			this._onKeydown = this._onKeydown.bind(this);
			window.addEventListener('message', this._onMessage);
			window.addEventListener('hashchange', this._onHashChange);
			window.addEventListener('beforeunload', this._onBeforeUnload);
			document.addEventListener('keydown', this._onKeydown);
		}
		if (this.pict && this.pict.CSSMap && (typeof this.pict.CSSMap.addCSS === 'function'))
		{
			this.pict.CSSMap.addCSS('DocumentFrameSwitcher-CSS', _CSS, 525);
		}
	}

	/** @return {any} The HeadlightDocumentRender service. */
	get render()
	{
		return this.pict.HeadlightDocumentRender;
	}

	// --- owner lifecycle ---------------------------------------------------------------------------------------------

	/**
	 * Bind the switcher to a record and the anchor it should cover. Frames already open for the same owner are kept;
	 * a different owner starts clean.
	 *
	 * @param {Object} pOptions
	 * @param {String} pOptions.OwnerKey - Identifies the record, e.g. `Sample:<GUID>`.
	 * @param {(pHash: String) => boolean} pOptions.OwnerMatch - Whether a location hash still belongs to the record.
	 * @param {String} pOptions.AnchorID - The id of the element the switcher is laid over.
	 * @param {Array<SwitcherDocument>} pOptions.Documents
	 * @param {String} [pOptions.RailNote] - Plain text shown at the foot of the document list.
	 * @return {void}
	 */
	attach(pOptions)
	{
		if (this.OwnerKey && (this.OwnerKey !== pOptions.OwnerKey))
		{
			this.teardown();
		}
		this.OwnerKey = pOptions.OwnerKey;
		this._ownerMatch = (typeof pOptions.OwnerMatch === 'function') ? pOptions.OwnerMatch : (() => true);
		this._anchorID = pOptions.AnchorID;
		this._ensureLayer();
		this.RailNote = String(pOptions.RailNote || '');
		this.setDocuments(pOptions.Documents || []);
		this._startPositionSync();
		try
		{
			this.pict.CSSMap.injectCSS();
		}
		catch (pError)
		{
			this.pict.log.warn(`[DocFrames] could not inject styles: ${pError.message || pError}`);
		}
	}

	/**
	 * @param {Array<SwitcherDocument>} pDocuments
	 * @return {void}
	 */
	setDocuments(pDocuments)
	{
		this.Documents = (Array.isArray(pDocuments) ? pDocuments : []).filter((pDocument) => pDocument && (Number(pDocument.IDDocument) > 0));
		this._renderChrome();
	}

	/** Close every frame and release the record. Unsaved work in them is gone; callers guard with hasUnsavedWork(). */
	teardown()
	{
		for (const tmpEntry of Object.values(this.Frames))
		{
			this._removeFrame(tmpEntry);
		}
		this.Frames = {};
		this._framesByFrameID = {};
		this.ActiveID = 0;
		this.Fullscreen = false;
		this.OwnerKey = null;
		this.Documents = [];
		this.RailNote = '';
		this._destroyRailTooltips();
		this._railMarkup = null;
		this._stopPositionSync();
		if (this._layer)
		{
			this._layer.style.display = 'none';
			this._layer.classList.remove('dfs-is-fs');
		}
		this._renderChrome();
	}

	// --- opening and switching ---------------------------------------------------------------------------------------

	/**
	 * Show a document, rendering it into a new frame the first time.
	 *
	 * @param {number|String} pIDDocument
	 * @return {void}
	 */
	open(pIDDocument)
	{
		const tmpID = Number(pIDDocument) || 0;
		if (!tmpID)
		{
			return;
		}
		const tmpExisting = this.Frames[tmpID];
		if (tmpExisting && (tmpExisting.Phase !== 'error'))
		{
			this._activate(tmpExisting);
			return;
		}
		if (tmpExisting)
		{
			this._removeFrame(tmpExisting);
		}
		const tmpEntry = this._createEntry(tmpID);
		this._activate(tmpEntry);
		this._closeIdleFrames();
		this._renderIntoFrame(tmpEntry);
	}

	/**
	 * @param {number} pIDDocument
	 * @return {FrameEntry}
	 */
	_createEntry(pIDDocument)
	{
		this._frameSerial++;
		/** @type {FrameEntry} */
		const tmpEntry =
		{
			IDDocument: pIDDocument,
			FrameID: `doc-${pIDDocument}-${this._frameSerial}`,
			Phase: 'rendering',
			Kind: 'pending',
			Error: '',
			IFrame: null,
			State: null,
			Touched: false,
			LastActiveAt: Date.now(),
			Timing: { ClickedAt: this._now(), RenderMS: null, LoadMS: null, ReadyMS: null, FormLoad: null },
		};
		this.Frames[pIDDocument] = tmpEntry;
		this._framesByFrameID[tmpEntry.FrameID] = tmpEntry;
		return tmpEntry;
	}

	/**
	 * Render the document on the server and point a new iframe at the result. The session token lets the page use the
	 * document data the render put in it instead of fetching it again.
	 *
	 * @param {FrameEntry} pEntry
	 * @return {Promise<void>}
	 */
	async _renderIntoFrame(pEntry)
	{
		const tmpRender = this.render;
		if (!tmpRender)
		{
			this._failEntry(pEntry, 'The document viewer is not available.');
			return;
		}
		let tmpResult;
		let tmpToken = null;
		try
		{
			[ tmpResult, tmpToken ] = await Promise.all(
				[
					new Promise((fResolve, fReject) =>
					{
						tmpRender.renderDocument(pEntry.IDDocument, 'default', (pError, pResult) =>
						{
							if (pError)
							{
								fReject(pError);
								return;
							}
							fResolve(pResult);
						});
					}),
					new Promise((fResolve) =>
					{
						tmpRender.getCheckoutSessionToken((pError, pToken) =>
						{
							fResolve((!pError && pToken && pToken.Token) ? pToken.Token : null);
						});
					}),
				]);
		}
		catch (pError)
		{
			this._failEntry(pEntry, `Could not open the document: ${(pError && pError.message) ? pError.message : pError}`);
			return;
		}
		if (this.Frames[pEntry.IDDocument] !== pEntry)
		{
			// Closed (or replaced) while the render was in flight.
			return;
		}
		pEntry.Timing.RenderMS = Math.round(this._now() - pEntry.Timing.ClickedAt);
		const tmpParameters = new URLSearchParams();
		if (tmpToken)
		{
			tmpParameters.set('SessionToken', tmpToken);
		}
		tmpParameters.set(FRAME_PARAMETER, pEntry.FrameID);
		const tmpURL = `${tmpResult.IndexURL}${(tmpResult.IndexURL.indexOf('?') > -1) ? '&' : '?'}${tmpParameters.toString()}`;

		const tmpIFrame = document.createElement('iframe');
		tmpIFrame.className = 'dfs-frame';
		tmpIFrame.title = this._documentLabel(pEntry.IDDocument);
		tmpIFrame.setAttribute('data-id-document', String(pEntry.IDDocument));
		// Forms scan QR codes with the camera and stamp locations, as they do when opened on their own.
		tmpIFrame.setAttribute('allow', 'camera; geolocation; clipboard-read; clipboard-write; fullscreen');
		tmpIFrame.addEventListener('load', () =>
		{
			this._onFrameLoad(pEntry);
		});
		tmpIFrame.src = tmpURL;
		pEntry.IFrame = tmpIFrame;
		pEntry.Phase = 'loading';
		this._ensureLayer();
		const tmpFrames = this._layer.querySelector('.dfs-frames');
		tmpFrames.appendChild(tmpIFrame);
		this._syncFrameVisibility();
		this._renderChrome();
	}

	/**
	 * @param {FrameEntry} pEntry
	 * @return {void}
	 */
	_onFrameLoad(pEntry)
	{
		if (pEntry.Timing.LoadMS === null)
		{
			pEntry.Timing.LoadMS = Math.round(this._now() - pEntry.Timing.ClickedAt);
		}
		let tmpWindow = null;
		try
		{
			tmpWindow = pEntry.IFrame.contentWindow;
			// A frame that cannot report whether it holds unsaved work is guarded by whether anyone typed in it.
			tmpWindow.document.addEventListener('input', () =>
			{
				this._noteTouched(pEntry);
			}, true);
			tmpWindow.document.addEventListener('change', () =>
			{
				this._noteTouched(pEntry);
			}, true);
		}
		catch (pError)
		{
			tmpWindow = null;
		}
		const tmpPict = tmpWindow && tmpWindow._Pict;
		if (tmpPict && tmpPict.providers && tmpPict.providers.DocumentFrame && tmpPict.providers.DocumentFrame.Enabled)
		{
			pEntry.Kind = 'form';
			if (pEntry.State && pEntry.State.Ready && !pEntry.State.ReloadPending)
			{
				pEntry.Phase = 'ready';
			}
		}
		else
		{
			pEntry.Kind = tmpPict ? 'untracked' : 'classic';
			pEntry.Phase = 'ready';
			pEntry.State = null;
			this._recordTiming(pEntry);
		}
		this._rememberKind(pEntry);
		this._renderChrome();
	}

	/**
	 * @param {FrameEntry} pEntry
	 * @return {void}
	 */
	_noteTouched(pEntry)
	{
		if (pEntry.Kind === 'form')
		{
			return;
		}
		if (!pEntry.Touched)
		{
			pEntry.Touched = true;
			this._renderChrome();
		}
	}

	/**
	 * @param {FrameEntry} pEntry
	 * @param {String} pMessage
	 * @return {void}
	 */
	_failEntry(pEntry, pMessage)
	{
		pEntry.Phase = 'error';
		pEntry.Error = pMessage;
		this.pict.log.error(`[DocFrames] document ${pEntry.IDDocument}: ${pMessage}`);
		this._renderChrome();
	}

	/**
	 * @param {FrameEntry} pEntry
	 * @return {void}
	 */
	_activate(pEntry)
	{
		this.ActiveID = pEntry.IDDocument;
		pEntry.LastActiveAt = Date.now();
		this._syncFrameVisibility();
		this._renderChrome();
	}

	/** Close the least recently viewed frames past MAX_LIVE_FRAMES that are safe to close. */
	_closeIdleFrames()
	{
		const tmpEntries = Object.values(this.Frames);
		if (tmpEntries.length <= MAX_LIVE_FRAMES)
		{
			return;
		}
		const tmpClosable = tmpEntries
			.filter((pEntry) => (pEntry.IDDocument !== this.ActiveID) && this._isSafeToClose(pEntry))
			.sort((pA, pB) => pA.LastActiveAt - pB.LastActiveAt);
		let tmpExcess = tmpEntries.length - MAX_LIVE_FRAMES;
		for (const tmpEntry of tmpClosable)
		{
			if (tmpExcess <= 0)
			{
				break;
			}
			this.pict.log.info(`[DocFrames] closing idle document ${tmpEntry.IDDocument} (more than ${MAX_LIVE_FRAMES} open).`);
			this._removeFrame(tmpEntry);
			delete this.Frames[tmpEntry.IDDocument];
			tmpExcess--;
		}
		if (tmpExcess > 0)
		{
			this.pict.log.warn(`[DocFrames] ${tmpEntries.length} documents open; the rest hold unsaved work or are still loading, so they stay open.`);
		}
	}

	/**
	 * @param {FrameEntry} pEntry
	 * @return {boolean}
	 */
	_isSafeToClose(pEntry)
	{
		if (pEntry.Phase === 'error')
		{
			return true;
		}
		if (pEntry.Phase !== 'ready')
		{
			return false;
		}
		return !this._unsavedReason(pEntry);
	}

	/**
	 * @param {FrameEntry} pEntry
	 * @return {void}
	 */
	_removeFrame(pEntry)
	{
		if (pEntry.IFrame && pEntry.IFrame.parentNode)
		{
			pEntry.IFrame.parentNode.removeChild(pEntry.IFrame);
		}
		pEntry.IFrame = null;
		delete this._framesByFrameID[pEntry.FrameID];
		if (this.ActiveID === pEntry.IDDocument)
		{
			this.ActiveID = 0;
		}
	}

	// --- actions from the chrome -------------------------------------------------------------------------------------

	/** Reload the active document from the server. Asks first when it holds unsaved work. */
	reloadActive()
	{
		const tmpEntry = this.Frames[this.ActiveID];
		if (!tmpEntry)
		{
			return;
		}
		if (this._unsavedReason(tmpEntry) && !window.confirm(`Reload "${this._documentLabel(tmpEntry.IDDocument)}"? Your unsaved changes in it will be lost.`))
		{
			return;
		}
		const tmpID = tmpEntry.IDDocument;
		this._removeFrame(tmpEntry);
		delete this.Frames[tmpID];
		this.open(tmpID);
	}

	/** Open the active document in its own browser tab, the longhand way. */
	openActiveInNewTab()
	{
		const tmpID = this.ActiveID;
		if (!tmpID || !this.render || (typeof this.render.openDocument !== 'function'))
		{
			return;
		}
		this.render.openDocument(tmpID, { target: '_blank', variation: 'default' }, (pError) =>
		{
			if (pError)
			{
				this.pict.log.error(`[DocFrames] opening document ${tmpID} in a new tab failed: ${pError.message || pError}`);
			}
		});
	}

	/** Toggle the switcher between its place in the page and the whole browser view. */
	toggleFullscreen()
	{
		this.Fullscreen = !this.Fullscreen;
		if (this._layer)
		{
			this._layer.classList.toggle('dfs-is-fs', this.Fullscreen);
		}
		this._syncLayerPosition();
		this._renderChrome();
	}

	/**
	 * Save every open form that reports unsaved changes, through its own Save.
	 *
	 * @return {Promise<Array<{ IDDocument: number, Error: ?String }>>}
	 */
	async saveAllUnsaved()
	{
		const tmpTargets = Object.values(this.Frames).filter((pEntry) => (pEntry.Kind === 'form') && pEntry.State && pEntry.State.HasUnsavedChanges);
		const tmpResults = await Promise.all(tmpTargets.map(async (pEntry) =>
		{
			const tmpReply = await this._request(pEntry, 'save');
			if (tmpReply && tmpReply.state)
			{
				pEntry.State = tmpReply.state;
			}
			return { IDDocument: pEntry.IDDocument, Error: tmpReply ? tmpReply.error : 'The form did not answer.' };
		}));
		this._renderChrome();
		return tmpResults;
	}

	/**
	 * The bar's Save button: save every open form with unsaved changes and say how it went.
	 * @return {Promise<void>}
	 */
	async saveAllFromChrome()
	{
		const tmpResults = await this.saveAllUnsaved();
		const tmpFailed = tmpResults.filter((pResult) => pResult.Error);
		if (tmpFailed.length)
		{
			this._toast(`Could not save ${tmpFailed.map((pResult) => `"${this._documentLabel(pResult.IDDocument)}"`).join(', ')}.`, 'error');
		}
		else if (tmpResults.length)
		{
			this._toast(tmpResults.length === 1 ? 'Document saved.' : `${tmpResults.length} documents saved.`, 'success');
		}
	}

	// --- unsaved-work guard ------------------------------------------------------------------------------------------

	/**
	 * Why a frame may hold unsaved work, or '' when it does not.
	 *
	 * @param {FrameEntry} pEntry
	 * @return {String}
	 */
	_unsavedReason(pEntry)
	{
		if (!pEntry.IFrame)
		{
			return '';
		}
		if (pEntry.Kind === 'form')
		{
			if (pEntry.State && (pEntry.State.Saving || pEntry.State.ApprovalBusy))
			{
				return 'saving';
			}
			return (pEntry.State && pEntry.State.HasUnsavedChanges) ? 'unsaved' : '';
		}
		return pEntry.Touched ? 'maybe' : '';
	}

	/** @return {Array<FrameEntry>} The frames that hold, or may hold, unsaved work. */
	framesWithUnsavedWork()
	{
		return Object.values(this.Frames).filter((pEntry) => !!this._unsavedReason(pEntry));
	}

	/** @return {boolean} */
	hasUnsavedWork()
	{
		return this.framesWithUnsavedWork().length > 0;
	}

	/**
	 * Ask before discarding open documents with unsaved work.
	 *
	 * @return {boolean} True when it is fine to close them.
	 */
	confirmDiscard()
	{
		const tmpFrames = this.framesWithUnsavedWork();
		if (!tmpFrames.length)
		{
			return true;
		}
		const tmpNames = tmpFrames.map((pEntry) => `• ${this._documentLabel(pEntry.IDDocument)}${(this._unsavedReason(pEntry) === 'maybe') ? ' (may have changes)' : ''}`).join('\n');
		return window.confirm(`These open documents have changes that are not saved:\n\n${tmpNames}\n\nLeave and lose them?`);
	}

	/** @param {BeforeUnloadEvent} pEvent */
	_onBeforeUnload(pEvent)
	{
		if (!this.hasUnsavedWork())
		{
			return;
		}
		pEvent.preventDefault();
		pEvent.returnValue = '';
	}

	/** Leaving the record (a hash it does not match) closes its frames, after asking when they hold unsaved work. */
	_onHashChange()
	{
		const tmpNewHash = window.location.hash;
		const tmpOldHash = this._lastHash;
		this._lastHash = tmpNewHash;
		if (this._restoringHash)
		{
			this._restoringHash = false;
			return;
		}
		if (!this.OwnerKey || this._ownerMatch(tmpNewHash))
		{
			return;
		}
		if (this.hasUnsavedWork() && !this.confirmDiscard())
		{
			this._restoringHash = true;
			this._lastHash = tmpOldHash;
			window.location.hash = tmpOldHash;
			return;
		}
		this.teardown();
	}

	/** @param {KeyboardEvent} pEvent */
	_onKeydown(pEvent)
	{
		if ((pEvent.key === 'Escape') && this.Fullscreen)
		{
			this.toggleFullscreen();
		}
	}

	// --- messages from the frames ------------------------------------------------------------------------------------

	/**
	 * A DocumentFrame message, trusted only from the frame it names and from this origin.
	 * @param {MessageEvent} pEvent
	 */
	_onMessage(pEvent)
	{
		const tmpData = pEvent.data;
		if (!tmpData || (tmpData.channel !== CHANNEL) || (pEvent.origin !== window.location.origin))
		{
			return;
		}
		const tmpEntry = this._framesByFrameID[tmpData.frameId];
		if (!tmpEntry || !tmpEntry.IFrame || (pEvent.source !== tmpEntry.IFrame.contentWindow))
		{
			return;
		}
		if (tmpData.requestId && this._pendingRequests[tmpData.requestId])
		{
			const tmpPending = this._pendingRequests[tmpData.requestId];
			delete this._pendingRequests[tmpData.requestId];
			clearTimeout(tmpPending.timer);
			tmpPending.resolve(tmpData);
		}
		if (tmpData.state)
		{
			tmpEntry.State = tmpData.state;
			// An approval action can change the status without the form reloading, so any state message may carry it.
			this._syncDocumentStatus(tmpEntry);
		}
		tmpEntry.Kind = 'form';
		if (tmpData.type === 'ready')
		{
			tmpEntry.Phase = 'ready';
			tmpEntry.Timing.FormLoad = tmpData.timing || null;
			this._recordTiming(tmpEntry);
			this._rememberKind(tmpEntry);
		}
		else if (tmpEntry.State && tmpEntry.State.ReloadPending)
		{
			// An approval action landed and the form is reloading itself; the next ready brings the new status.
			tmpEntry.Phase = 'loading';
		}
		this._renderChrome();
	}

	/**
	 * @param {FrameEntry} pEntry
	 * @param {String} pType
	 * @return {Promise<any>} The reply, or null if the frame did not answer in time.
	 */
	_request(pEntry, pType)
	{
		return new Promise((fResolve) =>
		{
			if (!pEntry.IFrame || !pEntry.IFrame.contentWindow)
			{
				fResolve(null);
				return;
			}
			this._requestSerial++;
			const tmpRequestID = `${pEntry.FrameID}-r${this._requestSerial}`;
			const tmpTimer = setTimeout(() =>
			{
				delete this._pendingRequests[tmpRequestID];
				fResolve(null);
			}, REQUEST_TIMEOUT_MS);
			this._pendingRequests[tmpRequestID] = { resolve: fResolve, timer: tmpTimer };
			pEntry.IFrame.contentWindow.postMessage({ channel: CHANNEL, frameId: pEntry.FrameID, type: pType, requestId: tmpRequestID }, window.location.origin);
		});
	}

	/**
	 * Carry the status a frame reports back onto the list row, so the rail stays right after an approval action.
	 *
	 * @param {FrameEntry} pEntry
	 * @return {void}
	 */
	_syncDocumentStatus(pEntry)
	{
		if (!pEntry.State || !pEntry.State.Status)
		{
			return;
		}
		const tmpDocument = this.Documents.find((pDocument) => Number(pDocument.IDDocument) === pEntry.IDDocument);
		if (tmpDocument)
		{
			tmpDocument.Status = pEntry.State.Status;
		}
	}

	/**
	 * @param {FrameEntry} pEntry
	 * @return {void}
	 */
	_rememberKind(pEntry)
	{
		const tmpDocument = this.Documents.find((pDocument) => Number(pDocument.IDDocument) === pEntry.IDDocument);
		if (tmpDocument && tmpDocument.DocumentType && (pEntry.Kind !== 'pending'))
		{
			this._kindByType[tmpDocument.DocumentType] = pEntry.Kind;
		}
	}

	/**
	 * One `[DocFrames]` line per open: click to server render, to the page's load event, to the form drawn — plus the
	 * form's own `[FormLoad]` phases when it reports them. Kept on `Timings` for a harness to read.
	 *
	 * @param {FrameEntry} pEntry
	 * @return {void}
	 */
	_recordTiming(pEntry)
	{
		if (pEntry.Timing.ReadyMS !== null)
		{
			return;
		}
		pEntry.Timing.ReadyMS = Math.round(this._now() - pEntry.Timing.ClickedAt);
		const tmpDocument = this.Documents.find((pDocument) => Number(pDocument.IDDocument) === pEntry.IDDocument) || {};
		const tmpRecord =
		{
			IDDocument: pEntry.IDDocument,
			DocumentType: tmpDocument.DocumentType || '',
			Kind: pEntry.Kind,
			RenderMS: pEntry.Timing.RenderMS,
			LoadMS: pEntry.Timing.LoadMS,
			ReadyMS: pEntry.Timing.ReadyMS,
			FormLoad: pEntry.Timing.FormLoad,
		};
		this.Timings.push(tmpRecord);
		const tmpFormLoad = (tmpRecord.FormLoad && tmpRecord.FormLoad.TotalMS !== undefined) ? `, form ${tmpRecord.FormLoad.TotalMS}ms from frame navigation` : '';
		this.pict.log.info(`[DocFrames] document ${tmpRecord.IDDocument} (${tmpRecord.DocumentType || 'unknown type'}, ${tmpRecord.Kind}): render ${tmpRecord.RenderMS}ms, page load ${tmpRecord.LoadMS}ms, ready ${tmpRecord.ReadyMS}ms after click${tmpFormLoad}`);
	}

	// --- layer, positioning and chrome -------------------------------------------------------------------------------

	/** Create the layer the frames live in, once, outside any markup a repaint replaces. */
	_ensureLayer()
	{
		if (this._layer && document.body.contains(this._layer))
		{
			return;
		}
		const tmpLayer = document.createElement('div');
		tmpLayer.id = LAYER_ID;
		tmpLayer.className = 'dfs';
		tmpLayer.style.display = 'none';
		tmpLayer.innerHTML = '<div class="dfs-stage"><div class="dfs-bar"></div><div class="dfs-frames"><div class="dfs-placeholder"></div></div></div><aside class="dfs-rail"></aside>';
		document.body.appendChild(tmpLayer);
		this._layer = tmpLayer;
		this._railMarkup = null;
	}

	/** Track the anchor every animation frame while a record is attached. */
	_startPositionSync()
	{
		if (this._rafHandle || (typeof window.requestAnimationFrame !== 'function'))
		{
			return;
		}
		const fTick = () =>
		{
			this._syncLayerPosition();
			this._rafHandle = window.requestAnimationFrame(fTick);
		};
		this._rafHandle = window.requestAnimationFrame(fTick);
	}

	/** Stop tracking the anchor. */
	_stopPositionSync()
	{
		if (this._rafHandle)
		{
			window.cancelAnimationFrame(this._rafHandle);
			this._rafHandle = null;
		}
	}

	/**
	 * The lowest point of the window a frame can use: the window's bottom edge, or the top of the app's Bulma fixed bottom
	 * navbar when one is showing.
	 *
	 * @return {number} - The visible bottom edge, in viewport pixels.
	 */
	_visibleBottom()
	{
		const tmpNavbar = document.querySelector('.navbar.is-fixed-bottom');
		if (tmpNavbar && (tmpNavbar.offsetHeight > 0))
		{
			return Math.min(window.innerHeight, tmpNavbar.getBoundingClientRect().top);
		}
		return window.innerHeight;
	}

	/**
	 * Lay the layer over the anchor while it is on screen; hide it (frames stay alive) while it is not. The layer stops at
	 * the visible bottom of the window, so a form's bottom bar is never below the fold or under the app's footer.
	 */
	_syncLayerPosition()
	{
		if (!this._layer)
		{
			return;
		}
		const tmpAnchor = this._anchorID ? document.getElementById(this._anchorID) : null;
		const tmpVisible = !!(tmpAnchor && tmpAnchor.offsetParent);
		if (!tmpVisible)
		{
			if (this._layer.style.display !== 'none')
			{
				this._layer.style.display = 'none';
			}
			if (this.Fullscreen)
			{
				this.Fullscreen = false;
				this._layer.classList.remove('dfs-is-fs');
			}
			return;
		}
		if (this._layer.style.display === 'none')
		{
			this._layer.style.display = '';
		}
		if (this.Fullscreen)
		{
			return;
		}
		const tmpRect = tmpAnchor.getBoundingClientRect();
		const tmpStyle = this._layer.style;
		const tmpTop = `${Math.round(tmpRect.top)}px`;
		const tmpLeft = `${Math.round(tmpRect.left)}px`;
		const tmpWidth = `${Math.round(tmpRect.width)}px`;
		const tmpHeight = `${Math.max(0, Math.round(Math.min(tmpRect.bottom, this._visibleBottom()) - tmpRect.top))}px`;
		if ((tmpStyle.top !== tmpTop) || (tmpStyle.left !== tmpLeft) || (tmpStyle.width !== tmpWidth) || (tmpStyle.height !== tmpHeight))
		{
			tmpStyle.top = tmpTop;
			tmpStyle.left = tmpLeft;
			tmpStyle.width = tmpWidth;
			tmpStyle.height = tmpHeight;
		}
	}

	/** Show the active frame; the others stay loaded but hidden. */
	_syncFrameVisibility()
	{
		for (const tmpEntry of Object.values(this.Frames))
		{
			if (tmpEntry.IFrame)
			{
				tmpEntry.IFrame.classList.toggle('dfs-frame-active', tmpEntry.IDDocument === this.ActiveID);
			}
		}
	}

	/** Repaint the rail, the bar and the placeholder. Never touches the frames. */
	_renderChrome()
	{
		if (!this._layer)
		{
			return;
		}
		const tmpRail = this._layer.querySelector('.dfs-rail');
		const tmpBar = this._layer.querySelector('.dfs-bar');
		const tmpPlaceholder = this._layer.querySelector('.dfs-placeholder');
		// The hover cards are part of what the rail shows, so a change to one alone repaints too.
		const tmpRailMarkup = [ this._railHTML() ].concat(this.Documents.map((pDocument) => (this._hasDetails(pDocument) ? this._tooltipHTML(pDocument) : ''))).join('\u0000');
		if (tmpRailMarkup !== this._railMarkup)
		{
			this._destroyRailTooltips();
			tmpRail.innerHTML = this._railHTML();
			this._railMarkup = tmpRailMarkup;
			this._attachRailTooltips(tmpRail);
		}
		tmpBar.innerHTML = this._barHTML();
		const tmpActive = this.Frames[this.ActiveID];
		if (!tmpActive)
		{
			tmpPlaceholder.style.display = '';
			tmpPlaceholder.innerHTML = this.Documents.length ?
				'<i class="fas fa-hand-pointer"></i><p>Choose a document on the right to open it here.</p>' :
				'<i class="fas fa-inbox"></i><p>No documents are attached to this sample.</p>';
		}
		else if (tmpActive.Phase === 'error')
		{
			tmpPlaceholder.style.display = '';
			tmpPlaceholder.innerHTML = `<i class="fas fa-triangle-exclamation"></i><p>${this._esc(tmpActive.Error)}</p><button class="button is-small is-light" onclick="_Pict.providers.DocumentFrameSwitcher.reloadActive()">Try again</button>`;
		}
		else if (!tmpActive.IFrame || (tmpActive.Phase !== 'ready'))
		{
			// The frame paints its own loading splash once it has a page; until then say what is happening.
			tmpPlaceholder.style.display = tmpActive.IFrame ? 'none' : '';
			tmpPlaceholder.innerHTML = '<span class="dfs-spinner"></span><p>Opening the document…</p>';
		}
		else
		{
			tmpPlaceholder.style.display = 'none';
		}
	}

	/** @return {String} The bar above the active document. */
	_barHTML()
	{
		const tmpEntry = this.Frames[this.ActiveID];
		const tmpUnsavedCount = Object.values(this.Frames).filter((pFrame) => (pFrame.Kind === 'form') && pFrame.State && pFrame.State.HasUnsavedChanges).length;
		const tmpSaveLabel = (tmpUnsavedCount === 1) ? '1 unsaved document' : `${tmpUnsavedCount} unsaved documents`;
		const tmpSaveAll = tmpUnsavedCount ?
			`<button class="dfs-btn dfs-btn-primary" onclick="_Pict.providers.DocumentFrameSwitcher.saveAllFromChrome()" title="Save every open document with unsaved changes"><i class="fas fa-floppy-disk"></i> Save ${tmpSaveLabel}</button>` :
			'';
		const tmpTitle = tmpEntry ? `<span class="dfs-bar-title">${this._esc(this._documentLabel(tmpEntry.IDDocument))}</span>${(tmpEntry.Kind === 'classic') ? '<span class="dfs-badge">Classic form</span>' : ''}` : '<span class="dfs-bar-title dfs-muted">Documents</span>';
		const tmpDocActions = tmpEntry ?
			`<button class="dfs-btn" onclick="_Pict.providers.DocumentFrameSwitcher.reloadActive()" title="Reload this document from the server"><i class="fas fa-rotate"></i></button>
				<button class="dfs-btn" onclick="_Pict.providers.DocumentFrameSwitcher.openActiveInNewTab()" title="Open in a new tab"><i class="fas fa-up-right-from-square"></i></button>` :
			'';
		const tmpFullscreen = `<button class="dfs-btn" onclick="_Pict.providers.DocumentFrameSwitcher.toggleFullscreen()" title="${this.Fullscreen ? 'Exit full screen (Esc)' : 'Full screen'}"><i class="fas ${this.Fullscreen ? 'fa-compress' : 'fa-expand'}"></i></button>`;
		return `<div class="dfs-bar-left">${tmpTitle}</div><div class="dfs-bar-right">${tmpSaveAll}${tmpDocActions}${tmpFullscreen}</div>`;
	}

	/** @return {String} The quick-switch list of the record's documents. */
	_railHTML()
	{
		const tmpNote = this.RailNote ? `<div class="dfs-rail-note"><i class="fas fa-lock"></i><span>${this._esc(this.RailNote)}</span></div>` : '';
		if (!this.Documents.length)
		{
			return `<div class="dfs-rail-empty">No documents</div>${tmpNote}`;
		}
		const tmpItems = this.Documents.map((pDocument) =>
		{
			const tmpID = Number(pDocument.IDDocument);
			const tmpEntry = this.Frames[tmpID];
			const tmpKind = tmpEntry && (tmpEntry.Kind !== 'pending') ? tmpEntry.Kind : (this._kindByType[pDocument.DocumentType] || '');
			const tmpStatus = (tmpEntry && tmpEntry.State && tmpEntry.State.Status) || pDocument.Status || '';
			const tmpClasses = [ 'dfs-item' ];
			if (tmpID === this.ActiveID)
			{
				tmpClasses.push('is-active');
			}
			if (tmpKind === 'classic')
			{
				tmpClasses.push('is-classic');
			}
			const tmpTitle = this._hasDetails(pDocument) ? '' : ` title="${this._esc(this._documentLabel(tmpID))}"`;
			return `<button class="${tmpClasses.join(' ')}" data-dfs-id="${tmpID}" onclick="_Pict.providers.DocumentFrameSwitcher.open(${tmpID})"${tmpTitle}>
				<span class="dfs-item-main">
					<span class="dfs-item-name">${this._esc(pDocument.Name || `Document #${tmpID}`)}</span>
					<span class="dfs-item-meta">${this._testsHTML(pDocument)}${(tmpKind === 'classic') ? ' · Classic' : ''}</span>
				</span>
				<span class="dfs-item-side">
					${tmpStatus ? `<span class="dfs-chip">${this._esc(tmpStatus)}</span>` : ''}
					${this._indicatorHTML(tmpEntry)}
				</span>
			</button>`;
		}).join('');
		return `<div class="dfs-rail-head">Documents <span class="dfs-muted">${this.Documents.length}</span></div><div class="dfs-rail-list">${tmpItems}</div>${tmpNote}`;
	}

	/**
	 * @param {?FrameEntry} pEntry
	 * @return {String}
	 */
	_indicatorHTML(pEntry)
	{
		if (!pEntry)
		{
			return '';
		}
		if (pEntry.Phase === 'error')
		{
			return '<i class="fas fa-triangle-exclamation dfs-ind dfs-ind-error" title="Could not open"></i>';
		}
		if ((pEntry.Phase === 'rendering') || (pEntry.Phase === 'loading'))
		{
			return '<span class="dfs-spinner dfs-ind" title="Opening"></span>';
		}
		const tmpReason = this._unsavedReason(pEntry);
		if (tmpReason === 'saving')
		{
			return '<span class="dfs-spinner dfs-ind" title="Saving"></span>';
		}
		if (tmpReason === 'unsaved')
		{
			return '<span class="dfs-dot dfs-dot-unsaved" title="Unsaved changes"></span>';
		}
		if (tmpReason === 'maybe')
		{
			return '<span class="dfs-dot dfs-dot-maybe" title="Edited here; this form saves on its own terms"></span>';
		}
		if (pEntry.State && (pEntry.State.Locked || !pEntry.State.CanEdit))
		{
			return `<i class="fas ${pEntry.State.Locked ? 'fa-lock' : 'fa-eye'} dfs-ind" title="${pEntry.State.Locked ? 'Locked' : 'Read only'}"></i>`;
		}
		return '<span class="dfs-dot dfs-dot-open" title="Open"></span>';
	}

	/**
	 * The rail's second line: the document's test codes, or its form's name when it names none and the document's own
	 * name doesn't already say it. Never the form's hash. When some tests are the record's own, the others are muted.
	 * @param {SwitcherDocument} pDocument
	 * @return {String}
	 */
	_testsHTML(pDocument)
	{
		const tmpTests = Array.isArray(pDocument.Tests) ? pDocument.Tests : [];
		if (!tmpTests.length)
		{
			const tmpFormName = String(pDocument.FormName || '').trim();
			return (tmpFormName && (tmpFormName.toLowerCase() !== String(pDocument.Name || '').trim().toLowerCase())) ? this._esc(tmpFormName) : '';
		}
		const tmpAnyAssigned = tmpTests.some((pTest) => pTest.Assigned);
		return tmpTests.map((pTest) => `<span class="dfs-test${(tmpAnyAssigned && !pTest.Assigned) ? ' is-other' : ''}">${this._esc(pTest.Code)}</span>`).join('<span class="dfs-test-sep"> · </span>');
	}

	/**
	 * @param {SwitcherDocument} pDocument
	 * @return {boolean} Whether the document has tests or media to show in a tooltip.
	 */
	_hasDetails(pDocument)
	{
		return !!(this._modal() && ((Array.isArray(pDocument.Tests) && pDocument.Tests.length) || this._mediaEntries(pDocument).length));
	}

	/** @return {any} pict-section-modal, when the app has it. */
	_modal()
	{
		const tmpModal = this.pict.views && this.pict.views['Pict-Section-Modal'];
		return (tmpModal && (typeof tmpModal.richTooltip === 'function')) ? tmpModal : null;
	}

	/**
	 * @param {SwitcherDocument} pDocument
	 * @return {Array<{ Kind: String, Count: number }>} The document's media, by kind, in display order.
	 */
	_mediaEntries(pDocument)
	{
		const tmpMedia = pDocument.Media || {};
		return [ 'image', 'video', 'audio', 'file' ].map((pKind) => ({ Kind: pKind, Count: Number(tmpMedia[pKind]) || 0 })).filter((pEntry) => pEntry.Count > 0);
	}

	/**
	 * The hover card for a document: its form, its tests (the record's own marked, with their results) and its media counts.
	 * @param {SwitcherDocument} pDocument
	 * @return {String}
	 */
	_tooltipHTML(pDocument)
	{
		const tmpTests = Array.isArray(pDocument.Tests) ? pDocument.Tests : [];
		const tmpAnyAssigned = tmpTests.some((pTest) => pTest.Assigned);
		const tmpRows = tmpTests.map((pTest) =>
			`<tr class="${(tmpAnyAssigned && !pTest.Assigned) ? 'is-other' : ''}">
				<td class="dfs-tip-plan">${pTest.Assigned ? '<i class="fas fa-link" aria-label="Linked to this sample"></i>' : ''}</td>
				<td class="dfs-tip-code">${this._esc(pTest.Code)}</td>
				<td>${this._esc(pTest.Name || '')}</td>
				<td class="dfs-tip-result">${pTest.Result ? `<span class="dfs-result is-${this._esc(pTest.ResultTone || 'info')}">${this._esc(pTest.Result)}</span>` : ''}</td>
			</tr>`).join('');
		const tmpMedia = this._mediaEntries(pDocument).map((pEntry) =>
		{
			const tmpLabel = `${pEntry.Count} ${MEDIA_LABELS[pEntry.Kind][(pEntry.Count === 1) ? 0 : 1]}`;
			return `<span class="dfs-tip-media-item"><i class="fas ${MEDIA_LABELS[pEntry.Kind][2]}"></i> ${this._esc(tmpLabel)}</span>`;
		}).join('');
		return [
			`<div class="dfs-tip-title">${this._esc(pDocument.FormName || pDocument.Name || '')}</div>`,
			tmpRows ? `<table class="dfs-tip-tests">${tmpRows}</table>` : '',
			tmpMedia ? `<div class="dfs-tip-media">${tmpMedia}</div>` : '',
		].join('');
	}

	/**
	 * Attach a hover card to each rail item that has tests or media.
	 * @param {HTMLElement} pRail
	 */
	_attachRailTooltips(pRail)
	{
		const tmpModal = this._modal();
		if (!tmpModal)
		{
			return;
		}
		for (const tmpDocument of this.Documents)
		{
			const tmpItem = pRail.querySelector(`.dfs-item[data-dfs-id="${Number(tmpDocument.IDDocument)}"]`);
			if (tmpItem && this._hasDetails(tmpDocument))
			{
				this._railTooltips.push(tmpModal.richTooltip(tmpItem, this._tooltipHTML(tmpDocument), { position: 'left', maxWidth: '360px', className: 'dfs-tip' }));
			}
		}
	}

	/** Remove the rail's hover cards, open or not. */
	_destroyRailTooltips()
	{
		for (const tmpTooltip of this._railTooltips)
		{
			if (tmpTooltip && (typeof tmpTooltip.destroy === 'function'))
			{
				tmpTooltip.destroy();
			}
		}
		this._railTooltips = [];
	}

	// --- helpers -----------------------------------------------------------------------------------------------------

	/**
	 * @param {number} pIDDocument
	 * @return {String}
	 */
	_documentLabel(pIDDocument)
	{
		const tmpDocument = this.Documents.find((pDocument) => Number(pDocument.IDDocument) === Number(pIDDocument));
		return (tmpDocument && tmpDocument.Name) ? tmpDocument.Name : `Document #${pIDDocument}`;
	}

	/** @return {number} Milliseconds, high resolution where available. */
	_now()
	{
		return ((typeof performance !== 'undefined') && (typeof performance.now === 'function')) ? performance.now() : Date.now();
	}

	/**
	 * @param {String} pMessage
	 * @param {String} pType - 'success' or 'error'.
	 */
	_toast(pMessage, pType)
	{
		try
		{
			const tmpPanels = this.pict.views.HeadlightPanels;
			if (tmpPanels && (typeof tmpPanels.toast === 'function'))
			{
				tmpPanels.toast({ type: (pType === 'error') ? 'is-danger' : 'is-success', message: pMessage });
				return;
			}
		}
		catch (pError)
		{
			// Fall through to the log.
		}
		this.pict.log.info(`[DocFrames] ${pMessage}`);
	}

	/**
	 * @param {any} pValue
	 * @return {String} The value, escaped for HTML text and attributes.
	 */
	_esc(pValue)
	{
		return String((pValue === undefined || pValue === null) ? '' : pValue)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
	}
}

const _CSS = /*css*/`
	.dfs { position: fixed; z-index: 25; display: flex; gap: 0.6rem; box-sizing: border-box; }
	.dfs.dfs-is-fs { inset: 0 !important; width: auto !important; height: auto !important; z-index: 1100; padding: 0.6rem; background: var(--theme-color-background-tertiary, #f3f4f6); transform: translateZ(0); }
	.dfs-stage { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 10px; overflow: hidden; background: var(--theme-color-background-primary, #fff); }
	.dfs-bar { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.4rem 0.6rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); background: var(--theme-color-background-secondary, #f7f9fb); }
	.dfs-bar-left { display: flex; align-items: center; gap: 0.5rem; min-width: 0; }
	.dfs-bar-right { display: flex; align-items: center; gap: 0.35rem; }
	.dfs-bar-title { font-weight: 600; font-size: 0.9rem; color: var(--theme-color-text-primary, #1f2733); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.dfs-muted { color: var(--theme-color-text-muted, #8a94a3); font-weight: 500; }
	.dfs-badge { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; padding: 0.1rem 0.45rem; }
	.dfs-badge { border-radius: 999px; background: var(--theme-color-background-tertiary, #eef1f6); color: var(--theme-color-text-muted, #6b7686); border: 1px dashed var(--theme-color-border-medium, #d5dbe3); }
	.dfs-btn { display: inline-flex; align-items: center; gap: 0.35rem; font: inherit; font-size: 0.78rem; font-weight: 600; color: var(--theme-color-text-secondary, #45505f); }
	.dfs-btn { background: var(--theme-color-background-primary, #fff); border: 1px solid var(--theme-color-border-medium, #d5dbe3); border-radius: 7px; padding: 0.28rem 0.55rem; cursor: pointer; white-space: nowrap; }
	.dfs-btn:hover { border-color: var(--theme-color-brand-primary, #5650e6); color: var(--theme-color-brand-primary, #5650e6); }
	.dfs-btn-primary { color: #fff; background: var(--theme-color-brand-primary, #5650e6); border-color: var(--theme-color-brand-primary, #5650e6); }
	.dfs-btn-primary:hover { color: #fff; filter: brightness(1.08); }
	.dfs-frames { position: relative; flex: 1 1 auto; min-height: 0; }
	.dfs-frame { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; visibility: hidden; pointer-events: none; background: #fff; }
	.dfs-frame.dfs-frame-active { visibility: visible; pointer-events: auto; }
	.dfs-placeholder { position: absolute; inset: 0; z-index: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; }
	.dfs-placeholder { gap: 0.5rem; color: var(--theme-color-text-muted, #8a94a3); font-size: 0.9rem; background: var(--theme-color-background-primary, #fff); text-align: center; padding: 1rem; }
	.dfs-placeholder .fas { font-size: 1.6rem; opacity: 0.5; }
	.dfs-rail { flex: 0 0 17rem; display: flex; flex-direction: column; min-height: 0; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 10px; background: var(--theme-color-background-primary, #fff); overflow: hidden; }
	.dfs-rail-head { padding: 0.55rem 0.75rem; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
	.dfs-rail-list { flex: 1 1 auto; overflow-y: auto; padding: 0.3rem; display: flex; flex-direction: column; gap: 0.2rem; }
	.dfs-rail-empty { padding: 1rem; color: var(--theme-color-text-muted, #8a94a3); font-size: 0.85rem; }
	.dfs-rail-note { flex: 0 0 auto; display: flex; gap: 0.45rem; align-items: baseline; padding: 0.6rem 0.75rem; border-top: 1px solid var(--theme-color-border-light, #e8ebf0); font-size: 0.78rem; line-height: 1.35; color: var(--theme-color-text-muted, #6b7686); }
	.dfs-rail-note .fas { font-size: 0.72rem; opacity: 0.7; }
	.dfs-item { display: flex; align-items: center; justify-content: space-between; gap: 0.4rem; width: 100%; text-align: left; font: inherit; }
	.dfs-item { padding: 0.45rem 0.55rem; border: 1px solid transparent; border-radius: 8px; background: transparent; cursor: pointer; color: var(--theme-color-text-primary, #1f2733); }
	.dfs-item:hover { background: var(--theme-color-background-secondary, #f7f9fb); }
	.dfs-item.is-active { background: var(--theme-color-background-selected, #eef1f8); border-color: var(--theme-color-brand-primary, #5650e6); }
	.dfs-item.is-classic .dfs-item-name { font-style: italic; }
	.dfs-item-main { display: flex; flex-direction: column; min-width: 0; }
	.dfs-item-name { font-size: 0.84rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.dfs-item-meta { font-size: 0.7rem; color: var(--theme-color-text-muted, #8a94a3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.dfs-test { color: var(--theme-color-text-secondary, #45505f); font-weight: 600; }
	.dfs-test.is-other { color: var(--theme-color-text-muted, #8a94a3); font-weight: 500; }
	.pict-modal-tooltip.dfs-tip { z-index: 1120; font-size: 0.78rem; }
	.dfs-tip-title { font-weight: 700; margin-bottom: 0.3rem; }
	.dfs-tip-tests { border-collapse: collapse; }
	.dfs-tip-tests td { padding: 0.08rem 0.35rem 0.08rem 0; vertical-align: top; color: inherit; }
	.dfs-tip-tests tr.is-other { opacity: 0.6; }
	.dfs-tip-tests td.dfs-tip-plan { width: 1rem; color: var(--theme-color-brand-primary, #5650e6); }
	.dfs-tip-tests td.dfs-tip-result { white-space: nowrap; text-align: right; padding-left: 0.5rem; }
	.dfs-result { display: inline-block; font-size: 0.66rem; font-weight: 700; padding: 0.05rem 0.4rem; border-radius: 999px; --dfs-tone: var(--theme-color-status-info, #3e7bd6); }
	.dfs-result { color: var(--dfs-tone); background: color-mix(in srgb, var(--dfs-tone) 16%, transparent); border: 1px solid color-mix(in srgb, var(--dfs-tone) 40%, transparent); }
	.dfs-result.is-success { --dfs-tone: var(--theme-color-status-success, #2f8f5b); }
	.dfs-result.is-danger { --dfs-tone: var(--theme-color-status-danger, var(--theme-color-status-error, #c0392b)); }
	.dfs-result.is-warning { --dfs-tone: var(--theme-color-status-warning, #c98a12); }
	.dfs-result.is-muted { --dfs-tone: var(--theme-color-text-muted, #8a94a3); }
	.dfs-tip-code { font-weight: 700; white-space: nowrap; }
	.dfs-tip-media { display: flex; flex-wrap: wrap; gap: 0.3rem 0.75rem; margin-top: 0.4rem; padding-top: 0.35rem; border-top: 1px solid var(--theme-color-border-light, rgba(127,127,127,0.25)); }
	.dfs-tip-media-item { white-space: nowrap; }
	.dfs-tip-media-item .fas { opacity: 0.8; margin-right: 0.15rem; }
	.dfs-item-side { display: flex; align-items: center; gap: 0.35rem; flex: 0 0 auto; }
	.dfs-chip { font-size: 0.64rem; font-weight: 700; padding: 0.08rem 0.42rem; border-radius: 999px; background: var(--theme-color-background-tertiary, #eef1f6); color: var(--theme-color-text-secondary, #45505f); }
	.dfs-dot { width: 0.55rem; height: 0.55rem; border-radius: 50%; display: inline-block; }
	.dfs-dot-open { background: var(--theme-color-border-medium, #c5ccd6); }
	.dfs-dot-unsaved { background: #d8a13b; box-shadow: 0 0 0 2px rgba(216,161,59,0.25); }
	.dfs-dot-maybe { background: transparent; border: 2px solid #d8a13b; }
	.dfs-ind { font-size: 0.72rem; color: var(--theme-color-text-muted, #8a94a3); }
	.dfs-ind-error { color: #b23b3b; }
	.dfs-spinner { width: 0.8rem; height: 0.8rem; border-radius: 50%; border: 2px solid var(--theme-color-border-medium, #d5dbe3); border-top-color: var(--theme-color-brand-primary, #5650e6); display: inline-block; animation: dfs-spin 0.8s linear infinite; }
	.dfs-placeholder .dfs-spinner { width: 1.4rem; height: 1.4rem; }
	@keyframes dfs-spin { to { transform: rotate(360deg); } }
	@media (max-width: 900px) { .dfs { flex-direction: column-reverse; } .dfs-rail { flex: 0 0 auto; max-height: max(30%, 6.5rem); } .dfs-rail-list { min-height: 3.5rem; } }
`;

module.exports = DocumentFrameSwitcherProvider;
module.exports.default_configuration = { ProviderIdentifier: 'DocumentFrameSwitcher' };
module.exports.MAX_LIVE_FRAMES = MAX_LIVE_FRAMES;
